import {getMixpanelClientSync} from "../../mixpanel/lib/mixpanel-client";
import {formatMixpanelEventName} from "../../mixpanel/lib/mixpanel-format-event-name";
import {touchSessionUtmCookie} from "../../utm/lib/utm-attribution-client";

const DISCOVERY_APP_NAME = "Discovery";

export type DiscoveryLinkEventKey =
	| "aops__discovery__program_selected"
	| "aops__discovery__consultation_request_initiated";

/**
 * Extracts the program slug from a nav href, whether relative or absolute.
 * Not anchored to the start of the path — Discovery runs under a basePath
 * (`NEXT_PUBLIC_DISCOVERY_BASE_PATH`, e.g. "/discover"), so a real href looks
 * like "/discover/program/aops-online", not "/program/aops-online".
 */
export function programSlugFromHref(href: string): string | null {
	const pathname = href.split(/[?#]/)[0]!;
	return /\/program\/([^/]+)\/?$/.exec(pathname)?.[1] ?? null;
}

/**
 * True for Academy contact-us links and the Zoom consultation scheduler.
 * Relative `/contact-us` hrefs count too.
 */
export function isConsultationHref(href: string): boolean {
	const pathname = href.split(/[?#]/)[0]!;
	if (/^https?:\/\/scheduler\.zoom\.us\//i.test(href)) {
		return pathname.includes("aops-consultation");
	}
	return /\/contact-us\/?$/.test(pathname);
}

function discoveryEventDisplayName(eventKey: DiscoveryLinkEventKey): string {
	return eventKey
		.split("_")
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

function isExpectedKeepaliveFailure(error: unknown): boolean {
	if (typeof DOMException !== "undefined" && error instanceof DOMException) {
		if (error.name === "AbortError") {
			return true;
		}
	}
	if (!(error instanceof Error)) {
		return false;
	}
	if (error.name === "AbortError") {
		return true;
	}
	const message = error.message;
	return (
		message === "Failed to fetch" ||
		message === "Load failed" ||
		message === "The user aborted a request." ||
		message.includes("NetworkError")
	);
}

function sendDiscoveryEventBeacon(
	event: DiscoveryLinkEventKey,
	properties?: Record<string, unknown>
): void {
	if (typeof window === "undefined") {
		return;
	}

	const basePath = process.env.NEXT_PUBLIC_DISCOVERY_BASE_PATH ?? "";
	const referrer =
		typeof document !== "undefined" && document.referrer
			? document.referrer
			: null;
	const screenHeight = window.screen?.height;
	const screenWidth = window.screen?.width;
	void (async () => {
		try {
			const response = await fetch(`${basePath}/api/track-discovery-event`, {
				method: "POST",
				keepalive: true,
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({
					event,
					properties,
					referrer,
					...(typeof screenHeight === "number" ? {screenHeight} : {}),
					...(typeof screenWidth === "number" ? {screenWidth} : {}),
				}),
			});
			if (!response.ok) {
				console.error("Discovery event beacon failed:", response.status);
			}
		} catch (error) {
			if (isExpectedKeepaliveFailure(error)) {
				return;
			}
			console.error("Discovery event beacon request failed:", error);
		}
	})();
}

function trackDiscoveryLinkOnClient(
	event: DiscoveryLinkEventKey,
	properties: Record<string, unknown>
): void {
	if (typeof window === "undefined") {
		return;
	}
	touchSessionUtmCookie();
	const mixpanel = getMixpanelClientSync();
	if (!mixpanel) {
		return;
	}
	try {
		const eventName = event.includes("__")
			? event
			: discoveryEventDisplayName(event);
		mixpanel.track(
			formatMixpanelEventName(eventName, DISCOVERY_APP_NAME),
			{
				...properties,
				event_key: event,
				app: process.env.NEXT_PUBLIC_APP_NAME || "unknown",
			},
			{transport: "sendBeacon"}
		);
	} catch (error) {
		console.error("Failed to track Discovery nav Mixpanel event:", error);
	}
}

function emitDiscoveryLinkEvent(
	event: DiscoveryLinkEventKey,
	properties: Record<string, unknown>
): void {
	if (process.env.NEXT_PUBLIC_APP_NAME === "discovery") {
		sendDiscoveryEventBeacon(event, properties);
		return;
	}
	trackDiscoveryLinkOnClient(event, properties);
}

/**
 * Fires the Discovery funnel event for a header/footer `<a href>` click when
 * the destination is a program page or a consultation exit. Same-page hash
 * links are not tracked.
 *
 * Aops already has `/api/track-discovery-event`: a keepalive POST that
 * survives the `<a href>` unload and runs the server pipeline (Mixpanel +
 * GTM, plus identity / HQ staff / UTM). Other apps cannot call that route
 * (same-origin 403), so they send Mixpanel from the browser instead.
 *
 * A program click always uses `aops__discovery__program_selected`. The
 * Discovery app beacons it. Other apps send the same name from the browser,
 * with `app` set to this site (`careers`, `rec-tool`, …).
 */
export function trackDiscoveryLinkClick(
	href: string,
	ctaLocation: string
): void {
	const programSlug = programSlugFromHref(href);
	if (programSlug) {
		emitDiscoveryLinkEvent("aops__discovery__program_selected", {
			program: programSlug,
			cta_location: ctaLocation,
		});
		return;
	}
	if (isConsultationHref(href)) {
		emitDiscoveryLinkEvent("aops__discovery__consultation_request_initiated", {
			cta_location: ctaLocation,
		});
	}
}
